// Guided Tour Configuration for YayFi Dashboard
// This tour explains the navigation and key features

// Informative tours (current - just explain features)
export const getDashboardTourSteps = (navigate) => [
  {
    target: '.dashboard-welcome',
    content: (
      <div>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Welcome to Your Dashboard! 🎉</h3>
        <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
          This is your central hub for managing patient feedback and reviews. Let's take a tour through the navigation to show you what each page does.
        </p>
      </div>
    ),
    disableBeacon: true,
    placement: 'center',
  },
  {
    target: '.setup-checklist',
    content: (
      <div>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Setup Checklist ✅</h3>
        <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
          Track your onboarding progress here. Complete all items to fully activate your feedback collection system.
        </p>
      </div>
    ),
    placement: 'top',
  },
  {
    target: '.create-widget-button',
    content: (
      <div>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Quick Start 🚀</h3>
        <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
          Click this button to create your first feedback funnel. But first, let me show you around the navigation!
        </p>
      </div>
    ),
    placement: 'top',
  },
  {
    target: '#widgets-nav-item',
    content: (
      <div>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Widgets & Funnels 🎨</h3>
        <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
          Create and customize your patient feedback funnels. This is what patients see when they click your SMS link. Add your logo, colors, and branding.
        </p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '#automation-nav-item',
    content: (
      <div>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Automation Workflows 🤖</h3>
        <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
          The heart of YayFi! Set up SMS automation to automatically ask patients for feedback after appointments. Choose timing, frequency, and customize your message.
        </p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '#customer-reviews-nav-item',
    content: (
      <div>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Customer Reviews ⭐</h3>
        <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
          View all your Google Reviews scraped from your business profile. Track your online reputation and see what patients are saying publicly.
        </p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '#customer-feedback-nav-item',
    content: (
      <div>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Customer Feedback 💬</h3>
        <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
          Private feedback from patients who rated 1-3 stars. This helps you identify issues and improve your service before they become public reviews.
        </p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '#qr-codes-nav-item',
    content: (
      <div>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>QR Codes 📱</h3>
        <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
          Generate QR codes linked to your feedback funnels. Print them for your reception desk, waiting room, or business cards to collect in-person feedback.
        </p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '#cliniko-data-nav-item',
    content: (
      <div>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Cliniko Data 🔄</h3>
        <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
          View all data synced from your Cliniko PMS: businesses, practitioners, appointment types, and patients. Click "Sync Now" to refresh your data.
        </p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '#bookings-nav-item',
    content: (
      <div>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Bookings 📅</h3>
        <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
          View all synced appointments from Cliniko. Filter by status (completed, upcoming, cancelled) and search for specific patients.
        </p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '#settings-nav-item',
    content: (
      <div>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Settings ⚙️</h3>
        <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
          Manage your account details, SMS settings, and general platform preferences.
        </p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '.dashboard-welcome',
    content: (
      <div>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Tour Complete! 🎉</h3>
        <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
          That's the tour! You can now explore each page at your own pace. Click the floating help button anytime to see a guide for the current page.
        </p>
      </div>
    ),
    disableBeacon: true,
    placement: 'center',
  },
];

// Page-specific tour configurations
export const getPageTourSteps = (pathname) => {
  switch (pathname) {
    case '/':
    case '/default':
      return [
        {
          target: '.dashboard-welcome',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Dashboard Home 🏠</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Your starting point. See your setup progress and quick actions to get started with collecting patient feedback.
              </p>
            </div>
          ),
          disableBeacon: true,
          placement: 'center',
        },
        {
          target: '.setup-checklist',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Setup Checklist ✅</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Track your onboarding progress here. Complete all items to fully activate your feedback collection system.
              </p>
            </div>
          ),
          placement: 'top',
        },
        {
          target: '.create-widget-button',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Create Widget 🚀</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Click this button to create your first feedback funnel. Once done, set up your automation workflow to start collecting patient feedback automatically.
              </p>
            </div>
          ),
          placement: 'top',
        },
      ];

    case '/rate-my-business/widgets':
      return [
        {
          target: '.create-widget-button-page',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Create New Widget</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Click this button to create a new widget for email feedback. You can customize colors, add your logo, and set up the feedback question.
              </p>
            </div>
          ),
          placement: 'bottom',
        },
        {
          target: '.create-funnel-button-page',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Create Feedback Funnel</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Click this button to create an SMS link funnel. Patients click the link in your SMS to leave feedback.
              </p>
            </div>
          ),
          placement: 'bottom',
        },
        {
          target: '.widgets-table',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Your Widgets & Funnels</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Here you can see all your created widgets and funnels. You can edit, copy, set as default, or activate/deactivate them using the action buttons.
              </p>
            </div>
          ),
          placement: 'top',
        },
      ];

    case '/automation-workflows':
      return [
        {
          target: '.create-workflow-button',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Create Workflow</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Click here to create a new SMS automation. You'll choose appointment types, timing, and customize your message.
              </p>
            </div>
          ),
          placement: 'bottom',
        },
        {
          target: '.workflows-list',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Your Workflows</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                View all your automation workflows here. Toggle them ON/OFF to enable or disable SMS sending. Click to edit or delete.
              </p>
            </div>
          ),
          placement: 'top',
        },
      ];

    case '/rate-my-business/customer-reviews':
      return [
        {
          target: '.reviews-filter',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Filter Reviews 🔍</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Use these filters to find specific reviews by rating, date range, or search by reviewer name or comment.
              </p>
            </div>
          ),
          placement: 'bottom',
        },
        {
          target: '.reviews-stats',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Review Statistics 📊</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                View your review performance with star distribution charts, pie charts, and trend graphs. Track your overall rating and review volume over time.
              </p>
            </div>
          ),
          placement: 'top',
        },
        {
          target: '.reviews-table',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Reviews Table 📋</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Browse all your Google reviews here. Search, filter by page, and see detailed information about each review including rating, date, and content.
              </p>
            </div>
          ),
          placement: 'top',
        },
      ];

    case '/rate-my-business/customer-feedback':
      return [
        {
          target: '.feedback-filter',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Filter Feedback 🔍</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Use these filters to find specific feedback by date range. This page shows private feedback from patients who rated 1-3 stars.
              </p>
            </div>
          ),
          placement: 'bottom',
        },
        {
          target: '.feedback-stats',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Feedback Statistics 📊</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                View your feedback breakdown with positive vs negative charts and trend graphs. Track issues before they become public reviews.
              </p>
            </div>
          ),
          placement: 'top',
        },
        {
          target: '.feedback-table',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Feedback Table 📋</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Browse all private feedback here. Search, filter by page, and see detailed information about each feedback including rating, date, and content.
              </p>
            </div>
          ),
          placement: 'top',
        },
      ];

    case '/cliniko-data':
      return [
        {
          target: '.sync-now-button',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Sync Now</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Click this to manually refresh your data from Cliniko. Use it when you've made changes in Cliniko and want them reflected here.
              </p>
            </div>
          ),
          placement: 'bottom',
        },
      ];

    case '/bookings':
      return [
        {
          target: '.bookings-table',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Bookings Table 📅</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                View all synced appointments from Cliniko here. Filter by status (completed, upcoming, cancelled), search for specific patients, and see appointment details.
              </p>
            </div>
          ),
          placement: 'top',
        },
      ];

    case '/rate-my-business/qr-codes':
      return [
        {
          target: '.qr-codes-stats',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>QR Code Statistics �</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                View your QR code performance metrics including total codes created, total scans, and codes with logos.
              </p>
            </div>
          ),
          placement: 'top',
        },
        {
          target: '.qr-codes-list',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>QR Codes List 📱</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Manage all your QR codes here. Create new codes, view them, check scan statistics, and edit their design and settings.
              </p>
            </div>
          ),
          placement: 'top',
        },
      ];

    case '/settings':
      return [
        {
          target: '.settings-tabs',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Settings Tabs 📑</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Switch between General settings, SMS configuration, and Account management tabs.
              </p>
            </div>
          ),
          placement: 'bottom',
        },
        {
          target: '.settings-container',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Settings Panel ⚙️</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Configure your platform settings including account details, SMS provider settings, and general preferences.
              </p>
            </div>
          ),
          placement: 'top',
        },
      ];

    default:
      return [];
  }
};

// Actionable tour configurations (guide users through completing actions)
export const getActionableTourSteps = (pathname, navigate) => {
  // Start from dashboard and guide through the setup flow
  if (pathname === '/' || pathname === '/default') {
    return [
      {
        target: '.dashboard-welcome',
        content: (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Welcome to Setup Guide! 🎉</h3>
            <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
              Let's set up your feedback collection system. We'll guide you through creating a widget, setting up automation, and configuring SMS settings.
            </p>
          </div>
        ),
        placement: 'center',
        disableBeacon: true,
        action: () => navigate('/rate-my-business/widgets'),
      },
    ];
  }

  switch (pathname) {
    case '/rate-my-business/widgets':
      return [
        {
          target: '.create-widget-button-page',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Create Your First Widget 🎨</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Let's create your first feedback funnel. Click the "Add Widget" button to start.
              </p>
            </div>
          ),
          placement: 'bottom',
          waitForAction: true,
          actionPrompt: 'Click the "Add Widget" button to open the widget creation dialog.',
          actionHint: 'Look for the button at the top of the page.',
        },
        {
          target: '.widget-dialog',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Configure Your Widget ⚙️</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Fill in the widget details: name, colors, and feedback question. This is what patients will see when they click your SMS link.
              </p>
            </div>
          ),
          placement: 'center',
          disableBeacon: true,
          waitForAction: true,
          actionPrompt: 'Fill in the widget details and click "Save" to create your widget.',
          actionHint: 'Choose a name, select colors, and write a clear feedback question.',
        },
        {
          target: '.widgets-table',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Widget Created! ✅</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Great! Your widget is now in the list. Next, let's set up automation to send SMS to patients automatically.
              </p>
            </div>
          ),
          placement: 'top',
          action: () => navigate('/automation-workflows'),
        },
      ];

    case '/automation-workflows':
      return [
        {
          target: '.create-workflow-button',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Create Automation Workflow 🤖</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Now let's set up SMS automation. Click "Create Workflow" to automatically ask patients for feedback after appointments.
              </p>
            </div>
          ),
          placement: 'bottom',
          waitForAction: true,
          actionPrompt: 'Click the "Create Workflow" button to open the workflow creation dialog.',
          actionHint: 'Look for the button at the top of the page.',
        },
        {
          target: '.workflow-dialog',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Configure Your Workflow ⚙️</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Select appointment types, timing, and customize your SMS message. This determines when and how patients receive feedback requests.
              </p>
            </div>
          ),
          placement: 'center',
          disableBeacon: true,
          waitForAction: true,
          actionPrompt: 'Select appointment types, set timing, customize the message, and click "Save".',
              actionHint: 'Choose which appointments trigger SMS and when to send them.',
        },
        {
          target: '.workflows-list',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Automation Created! ✅</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Excellent! Your automation is now active. Let's configure your SMS settings next.
              </p>
            </div>
          ),
          placement: 'top',
          action: () => navigate('/settings'),
        },
      ];

    case '/settings':
      return [
        {
          target: '.settings-tabs',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Configure SMS Settings 📱</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Click on the "SMS" tab to configure your SMS provider settings.
              </p>
            </div>
          ),
          placement: 'bottom',
          waitForAction: true,
          actionPrompt: 'Click the "SMS" tab to access SMS configuration.',
          actionHint: 'Look for the tab at the top of the settings page.',
        },
        {
          target: '.cellcast-settings',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Buy an SMS Phone Number �</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Buy a phone number to start sending SMS to your patients.
              </p>
            </div>
          ),
          placement: 'top',
          waitForAction: true,
          actionPrompt: 'Select a phone number, then click "Buy" to continue.',
          actionHint: 'Choose a number from the list and click "Buy".',
        },
        {
          target: '.settings-container',
          content: (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Setup Complete! 🎉</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '0' }}>
                Congratulations! You've completed the setup. Your feedback collection system is now ready to collect patient reviews automatically.
              </p>
            </div>
          ),
          placement: 'center',
          disableBeacon: true,
        },
      ];

    default:
      return [];
  }
};

const TOUR_BASE_PATHS = [
  '/',
  '/default',
  '/rate-my-business/widgets',
  '/automation-workflows',
  '/rate-my-business/customer-reviews',
  '/rate-my-business/customer-feedback',
  '/cliniko-data',
  '/bookings',
  '/rate-my-business/qr-codes',
  '/settings'
];

export const getTourPath = (pathname) => {
  if (!pathname) return pathname;
  const base = TOUR_BASE_PATHS.find((p) => pathname === p || (p !== '/' && pathname.startsWith(p + '/')));
  return base || pathname;
};

export const getPageHelpContent = (pathname) => {
  const base = getTourPath(pathname);
  const content = {
    '/rate-my-business/widgets': {
      title: 'Widgets & Funnels',
      description: 'Create two different types of feedback collection.',
      bullets: [
        'Widgets — embed in emails or share as a branded feedback page.',
        'Funnels — create an SMS link that patients click to leave feedback.',
        'Save multiple widgets and funnels for different campaigns or appointment types.'
      ]
    },
    '/automation-workflows': {
      title: 'Automation Workflows',
      description: 'Set up automated SMS messages that ask patients for feedback after appointments.',
      bullets: [
        'Choose which appointment types trigger a review request.',
        'Set the timing and message for each SMS.',
        'Track sent messages and patient replies from the workflows list.'
      ]
    },
    '/settings': {
      title: 'Settings',
      description: 'Configure your account, SMS provider, and platform preferences.',
      bullets: [
        'Buy a phone number to send SMS.',
        'Update your business profile and contact details.',
        'Manage billing, plans, and user roles from the relevant tabs.'
      ]
    },
    '/rate-my-business/customer-reviews': {
      title: 'Customer Reviews',
      description: 'Track and manage your Google reviews.',
      bullets: [
        'Filter reviews by rating, date range, or search by reviewer name.',
        'View review statistics and star distribution.',
        'Read individual review details and respond accordingly.'
      ]
    },
    '/rate-my-business/customer-feedback': {
      title: 'Customer Feedback',
      description: 'Monitor private patient feedback collected through YayFi.',
      bullets: [
        'Filter feedback by date range and rating.',
        'Track positive vs negative feedback trends.',
        'Address issues before they become public reviews.'
      ]
    },
    '/cliniko-data': {
      title: 'Cliniko Data',
      description: 'Sync your Cliniko appointments, patients, and practitioners.',
      bullets: [
        'Manually sync the latest data from Cliniko.',
        'Verify appointment types and practitioner mappings.',
        'Use synced data to drive automation workflows.'
      ]
    },
    '/bookings': {
      title: 'Bookings',
      description: 'View all appointments synced from Cliniko.',
      bullets: [
        'Filter by status, date range, or patient name.',
        'See completed, upcoming, and cancelled appointments.',
        'Use appointment data to trigger SMS feedback requests.'
      ]
    },
    '/rate-my-business/qr-codes': {
      title: 'QR Codes',
      description: 'Create and manage QR codes for your feedback pages.',
      bullets: [
        'View QR code scan statistics.',
        'Create new branded QR codes.',
        'Edit design and copy links for sharing.'
      ]
    }
  };

  if (content[base]) return content[base];

  // Generic fallback for any page that has a tour configured
  if (base && base !== '/' && base !== '/default' && getPageTourSteps(base).length > 0) {
    return {
      title: 'Page Tour',
      description: 'Learn about the features on this page with a short guided tour.',
      bullets: []
    };
  }

  return null;
};

export const tourStyles = {
  options: {
    zIndex: 10000,
    primaryColor: '#1976d2',
    backgroundColor: '#ffffff',
    arrowColor: '#ffffff',
    textColor: '#333333',
    overlayColor: 'rgba(0, 0, 0, 0.5)',
  },
  tooltip: {
    fontSize: '14px',
    padding: '16px',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
  },
  tooltipContent: {
    padding: '0',
  },
  tooltipTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '8px',
    color: '#1976d2',
  },
  tooltipFooter: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  buttonBack: {
    marginRight: '8px',
    borderRadius: '8px',
    textTransform: 'none',
  },
  buttonNext: {
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
  },
  buttonSkip: {
    color: '#666666',
    textTransform: 'none',
  },
  spotlight: {
    borderRadius: '8px',
  },
};
